import {
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
	SearchOutlined,
	SettingOutlined,
} from "@ant-design/icons";
import { Button, Input, Modal, message, Popconfirm, Segmented, Select, Spin, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { buildingsApi } from "@/modules/objects/api/buildings.api";
import { getAdminUsers } from "@/modules/users/services/users.backend";
import { UzPhoneInput } from "@/shared/components/UzPhoneInput";
import type { AppRole } from "@/shared/config/navigation";
import {
	canCreateLead as canCreateLeadByRole,
	canManageLeadStatuses as canManageLeadStatusesByRole,
} from "@/shared/config/permissions";
import { toUzPhoneE164 } from "@/shared/utils/uz-phone";
import { LeadSourceBadge } from "../components/LeadSourceBadge";
import { LeadsKanbanBoard } from "../components/LeadsKanbanBoard";
import { LeadsTable } from "../components/LeadsTable";
import { sourceLabels } from "../data/leads.mock";
import {
	createLeadStatus,
	deleteLeadStatus,
	getLeadStatuses,
	updateLeadStatusById,
} from "../services/lead-statuses.api";
import {
	createLead,
	deleteLead,
	getLeads,
	getLeadsApiErrorMessage,
	updateLeadStatus,
} from "../services/leads.api";
import type {
	LeadDraft,
	LeadItem,
	LeadSource,
	LeadStatus,
	LeadStatusId,
	LeadViewMode,
} from "../types/lead.types";
import {
	applyLeadManagerDisplay,
	formatLeadActivity,
	getLeadManagerLabel,
	hydrateLeadDisplayData,
	preserveLeadManagerDisplay,
} from "../utils/lead-display";

const emptyDraft: LeadDraft = {
	buildingName: "",
	email: "",
	managerId: "",
	managerName: "",
	name: "",
	phone: "",
	source: "facebook",
	statusId: "",
};

const emptyStatusDraft: LeadStatus = {
	color: "#2474d6",
	id: "",
	name: "",
	nameRu: "",
	order: 1,
};

export function LeadsKanbanPage() {
	const navigate = useNavigate();
	const { role } = useOutletContext<{ role: AppRole }>();
	const { user } = useAuthStore();
	const [leads, setLeads] = useState<LeadItem[]>([]);
	const [statuses, setStatuses] = useState<LeadStatus[]>([]);
	const [viewMode, setViewMode] = useState<LeadViewMode>("kanban");
	const [buildingFilter, setBuildingFilter] = useState("all");
	const [sourceFilter, setSourceFilter] = useState<LeadSource | "all">("all");
	const [search, setSearch] = useState("");
	const [selectedLead, setSelectedLead] = useState<LeadItem | null>(null);
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [isStatusesOpen, setIsStatusesOpen] = useState(false);
	const [isStatusFormOpen, setIsStatusFormOpen] = useState(false);
	const [draggingLeadId, setDraggingLeadId] = useState<string>();
	const [draft, setDraft] = useState<LeadDraft>(emptyDraft);
	const [editingStatusId, setEditingStatusId] = useState<LeadStatusId | null>(null);
	const [statusDraft, setStatusDraft] = useState<LeadStatus>(emptyStatusDraft);
	const [loading, setLoading] = useState(true);
	const [buildings, setBuildings] = useState<{ id: string; name: string }[]>([]);
	const [managers, setManagers] = useState<{ id: string; fullName: string }[]>([]);
	const canCreateLead = canCreateLeadByRole(role);
	const canManageLeadStatuses = canManageLeadStatusesByRole(role);

	useEffect(() => {
		let cancelled = false;

		async function fetchData() {
			try {
				setLoading(true);
				const [leadsResult, statusesResult, buildingsResult, usersResult] = await Promise.all([
					getLeads({ limit: 100 }),
					getLeadStatuses(),
					buildingsApi.getBuildings().catch(() => [] as { id: string; name: string }[]),
					getAdminUsers().catch(() => ({
						items: [] as { id: string; fullName: string }[],
						meta: { total: 0, page: 1, limit: 20, totalPages: 1 },
					})),
				]);
				if (!cancelled) {
					setLeads(leadsResult.items);
					setStatuses(statusesResult);
					setBuildings(
						buildingsResult.map((b: { id: string; name: string }) => ({ id: b.id, name: b.name }))
					);
					setManagers(
						usersResult.items.map((u: { id: string; fullName: string }) => ({
							id: u.id,
							fullName: u.fullName,
						}))
					);
				}
			} catch (error) {
				if (!cancelled) {
					message.error(getLeadsApiErrorMessage(error, "Ma'lumotlarni yuklashda xatolik"));
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		}

		fetchData();

		return () => {
			cancelled = true;
		};
	}, []);

	const filteredLeads = useMemo(() => {
		const leadsWithDisplayData = hydrateLeadDisplayData(leads, buildings);
		const query = search.trim().toLowerCase();

		return leadsWithDisplayData.filter((lead) => {
			const matchesSearch =
				query.length === 0 ||
				lead.name.toLowerCase().includes(query) ||
				lead.phone.toLowerCase().includes(query);
			const matchesBuilding = buildingFilter === "all" || lead.buildingName === buildingFilter;
			const matchesSource = sourceFilter === "all" || lead.source === sourceFilter;

			return matchesSearch && matchesBuilding && matchesSource;
		});
	}, [buildingFilter, buildings, leads, search, sourceFilter]);

	const orderedStatuses = useMemo(
		() => [...statuses].sort((first, second) => first.order - second.order),
		[statuses]
	);

	const selectedLeadDisplay = useMemo(
		() =>
			selectedLead ? (hydrateLeadDisplayData([selectedLead], buildings)[0] ?? selectedLead) : null,
		[buildings, selectedLead]
	);

	const selectedStatus = selectedLeadDisplay
		? orderedStatuses.find((status) => status.id === selectedLeadDisplay.statusId)
		: undefined;

	const handleDropLead = async (leadId: string, statusId: LeadStatusId) => {
		setDraggingLeadId(undefined);

		try {
			const updated = await updateLeadStatus(leadId, statusId);
			setLeads((currentLeads) =>
				currentLeads.map((lead) =>
					lead.id === updated.id ? preserveLeadManagerDisplay(updated, lead) : lead
				)
			);
			message.success("Lead holati yangilandi");
		} catch (error) {
			message.error(getLeadsApiErrorMessage(error, "Holatni yangilashda xatolik"));
		}
	};

	const handleSelectedLeadStatusChange = async (statusId: LeadStatusId) => {
		if (!selectedLead) {
			return;
		}

		try {
			const updated = await updateLeadStatus(selectedLead.id, statusId);
			setSelectedLead((current) =>
				current?.id === updated.id ? preserveLeadManagerDisplay(updated, current) : current
			);
			setLeads((currentLeads) =>
				currentLeads.map((lead) =>
					lead.id === updated.id ? preserveLeadManagerDisplay(updated, lead) : lead
				)
			);
			message.success("Lead holati yangilandi");
		} catch (error) {
			message.error(getLeadsApiErrorMessage(error, "Holatni yangilashda xatolik"));
		}
	};

	const handleDeleteLead = async () => {
		if (!selectedLead) {
			return;
		}

		try {
			await deleteLead(selectedLead.id);
			setLeads((currentLeads) => currentLeads.filter((lead) => lead.id !== selectedLead.id));
			setSelectedLead(null);
			message.success("Lead o'chirildi");
		} catch (error) {
			message.error(getLeadsApiErrorMessage(error, "Leadni o'chirishda xatolik"));
		}
	};

	const openStatusForm = (status?: LeadStatus) => {
		if (!canManageLeadStatuses) {
			message.warning("Menejer lead holatlarini sozlay olmaydi.");
			return;
		}

		setEditingStatusId(status?.id ?? null);
		setStatusDraft(
			status ?? {
				...emptyStatusDraft,
				order: orderedStatuses.length + 1,
			}
		);
		setIsStatusFormOpen(true);
	};

	const handleSaveStatus = async () => {
		if (!canManageLeadStatuses) {
			message.warning("Menejer lead holatlarini sozlay olmaydi.");
			return;
		}

		if (!statusDraft.name.trim()) {
			message.warning("Nomi majburiy");
			return;
		}

		try {
			if (editingStatusId) {
				const updated = await updateLeadStatusById(editingStatusId, {
					nameUz: statusDraft.name.trim(),
					nameRu: statusDraft.nameRu.trim() || undefined,
					color: statusDraft.color.trim() || "#2474d6",
					indexOrder: statusDraft.order,
				});
				setStatuses((currentStatuses) =>
					currentStatuses.map((status) => (status.id === editingStatusId ? updated : status))
				);
				message.success("Holat yangilandi");
			} else {
				const created = await createLeadStatus({
					nameUz: statusDraft.name.trim(),
					nameRu: statusDraft.nameRu.trim() || undefined,
					color: statusDraft.color.trim() || "#2474d6",
					indexOrder: orderedStatuses.length + 1,
					orgId: user?.orgId ?? undefined,
				});
				setStatuses((currentStatuses) => [...currentStatuses, created]);
				message.success("Holat qo'shildi");
			}
		} catch (error) {
			message.error(getLeadsApiErrorMessage(error, "Holatni saqlashda xatolik"));
		}

		setIsStatusFormOpen(false);
		setEditingStatusId(null);
		setStatusDraft(emptyStatusDraft);
	};

	const handleDeleteStatus = async (statusId: LeadStatusId) => {
		if (!canManageLeadStatuses) {
			message.warning("Menejer lead holatlarini sozlay olmaydi.");
			return;
		}

		if (statuses.length === 1) {
			message.warning("Kamida bitta holat qolishi kerak");
			return;
		}

		try {
			await deleteLeadStatus(statusId);
			const nextStatuses = statuses.filter((status) => status.id !== statusId);
			const fallbackStatusId = nextStatuses[0]?.id;

			setStatuses(nextStatuses);
			if (fallbackStatusId) {
				setLeads((currentLeads) =>
					currentLeads.map((lead) =>
						lead.statusId === statusId ? { ...lead, statusId: fallbackStatusId } : lead
					)
				);
			}
			setDraft((current) =>
				current.statusId === statusId && fallbackStatusId
					? { ...current, statusId: fallbackStatusId }
					: current
			);
			setSelectedLead((current) =>
				current?.statusId === statusId && fallbackStatusId
					? { ...current, statusId: fallbackStatusId }
					: current
			);
			message.success("Holat o'chirildi");
		} catch (error) {
			message.error(getLeadsApiErrorMessage(error, "Holatni o'chirishda xatolik"));
		}
	};

	const openCreateLead = (statusId?: LeadStatusId) => {
		if (!canCreateLead) {
			message.warning("Super admin lead yarata olmaydi.");
			return;
		}

		setDraft((current) => ({ ...current, statusId: statusId ?? orderedStatuses[0]?.id ?? "" }));
		setIsCreateOpen(true);
	};

	const handleCreateLead = async () => {
		if (!(draft.name.trim() && draft.phone.trim())) {
			message.warning("Ism va telefon majburiy");
			return;
		}

		const normalizedPhone = toUzPhoneE164(draft.phone);
		if (!normalizedPhone) {
			message.warning("Telefon raqamni to'liq kiriting");
			return;
		}

		try {
			const newLead = await createLead(
				draft.name,
				normalizedPhone,
				draft.email,
				draft.source,
				draft.statusId || undefined,
				undefined,
				draft.managerId || undefined
			);
			const displayLead = applyLeadManagerDisplay(newLead, draft.managerName);
			setLeads((currentLeads) => [displayLead, ...currentLeads]);
			setDraft(emptyDraft);
			setIsCreateOpen(false);
			message.success("Lead qo'shildi");
		} catch (error) {
			message.error(getLeadsApiErrorMessage(error, "Lead qo'shishda xatolik"));
		}
	};

	return (
		<div className="crm-leads-page">
			<div className="crm-page-head">
				<div>
					<h1>Leadlar</h1>
					<p>Potentsial mijozlarni kanban yoki jadvalda kuzatish</p>
				</div>
				<div className="crm-head-actions crm-leads-actions">
					<Select
						options={[
							{ label: "Barcha binolar", value: "all" },
							...buildings.map((b) => ({ label: b.name, value: b.name })),
						]}
						value={buildingFilter}
						onChange={setBuildingFilter}
					/>
					<Select
						options={[
							{ label: "Manba: barchasi", value: "all" },
							...(Object.entries(sourceLabels) as [LeadSource, string][]).map(([value, label]) => ({
								label,
								value,
							})),
						]}
						value={sourceFilter}
						onChange={setSourceFilter}
					/>
					<Segmented
						options={[
							{ label: "Kanban", value: "kanban" },
							{ label: "Jadval", value: "table" },
						]}
						value={viewMode}
						onChange={(value) => setViewMode(value as LeadViewMode)}
					/>
					{canManageLeadStatuses ? (
						<Tooltip title="Kanban holatlari">
							<Button
								aria-label="Kanban holatlari"
								className="crm-lead-settings-btn"
								icon={<SettingOutlined />}
								onClick={() => setIsStatusesOpen(true)}
							/>
						</Tooltip>
					) : null}
					{canCreateLead ? (
						<Button
							className="crm-primary-btn"
							icon={<PlusOutlined />}
							onClick={() => openCreateLead()}
							type="primary"
						>
							Lead
						</Button>
					) : null}
				</div>
			</div>

			<div className="crm-leads-toolbar">
				<Input
					prefix={<SearchOutlined />}
					placeholder="Ism yoki telefon bo'yicha qidirish..."
					value={search}
					onChange={(event) => setSearch(event.target.value)}
				/>
				<div>
					<strong>{filteredLeads.length}</strong>
					<span>ta lead topildi</span>
				</div>
			</div>

			{loading ? (
				<div className="crm-card">
					<div className="crm-card-body" style={{ textAlign: "center", padding: "48px 0" }}>
						<Spin size="large" />
					</div>
				</div>
			) : viewMode === "kanban" ? (
				<LeadsKanbanBoard
					draggingLeadId={draggingLeadId}
					leads={filteredLeads}
					statuses={orderedStatuses}
					onDragStart={setDraggingLeadId}
					onAddLead={openCreateLead}
					onDropLead={handleDropLead}
					onOpenLead={setSelectedLead}
					canAddLead={canCreateLead}
				/>
			) : (
				<LeadsTable leads={filteredLeads} statuses={orderedStatuses} onOpenLead={setSelectedLead} />
			)}

			<div className="crm-card crm-leads-note">
				<div className="crm-card-body">
					Lead kartasini boshqa ustunga tortib, mijoz bilan ishlash bosqichini yangilang.
				</div>
			</div>

			<Modal
				centered
				className="crm-lead-detail-modal"
				destroyOnHidden
				footer={[
					<Button
						className="crm-danger-btn"
						icon={<DeleteOutlined />}
						key="delete"
						onClick={handleDeleteLead}
					>
						O'chirish
					</Button>,
					<Button key="close" onClick={() => setSelectedLead(null)}>
						Yopish
					</Button>,
					<Button
						className="crm-primary-btn"
						key="convert"
						onClick={() => {
							if (!selectedLead) {
								return;
							}

							navigate("/clients/new", { state: { lead: selectedLead } });
						}}
						type="primary"
					>
						Mijozga aylantirish
					</Button>,
				]}
				open={Boolean(selectedLeadDisplay)}
				title={selectedLeadDisplay?.name}
				width={650}
				onCancel={() => setSelectedLead(null)}
			>
				{selectedLeadDisplay ? (
					<div className="crm-lead-modal">
						<div className="crm-lead-modal-head">
							<span className="crm-lead-avatar">{selectedLeadDisplay.managerInitials}</span>
							<div>
								<strong>{selectedLeadDisplay.name}</strong>
								<div className="crm-lead-modal-tags">
									<LeadSourceBadge source={selectedLeadDisplay.source} />
									<span className="crm-status-badge" style={{ color: selectedStatus?.color }}>
										<span />
										{selectedStatus?.name}
									</span>
								</div>
							</div>
						</div>

						<div className="crm-lead-modal-list">
							<div>
								<span>Telefon</span>
								<strong>{selectedLeadDisplay.phone}</strong>
							</div>
							<div>
								<span>Manba</span>
								<strong>{selectedLeadDisplay.source}</strong>
							</div>
							<div>
								<span>Mas'ul menejer</span>
								<strong>{getLeadManagerLabel(selectedLeadDisplay)}</strong>
							</div>
							<div>
								<span>Javobsiz qo'ng'iroqlar</span>
								<strong>{selectedLeadDisplay.calls}</strong>
							</div>
						</div>

						<div className="crm-lead-status-control">
							<span>Holatni o'zgartirish</span>
							<Select
								options={orderedStatuses.map((status) => ({
									label: status.name,
									value: status.id,
								}))}
								value={selectedLeadDisplay.statusId}
								onChange={handleSelectedLeadStatusChange}
							/>
						</div>
						<div className="crm-lead-modal-grid">
							<div>
								<span>Oxirgi aktivlik</span>
								<strong>{formatLeadActivity(selectedLeadDisplay.lastActivity)}</strong>
							</div>
							{selectedLeadDisplay.buildingName ? (
								<div>
									<span>Bino</span>
									<strong>{selectedLeadDisplay.buildingName}</strong>
								</div>
							) : null}
							{selectedLeadDisplay.roomInterest ? (
								<div>
									<span>Qiziqish</span>
									<strong>{selectedLeadDisplay.roomInterest}</strong>
								</div>
							) : null}
							{selectedLeadDisplay.budget ? (
								<div>
									<span>Budjet</span>
									<strong>{selectedLeadDisplay.budget}</strong>
								</div>
							) : null}
						</div>
					</div>
				) : null}
			</Modal>

			<Modal
				centered
				className="crm-lead-settings-modal"
				destroyOnHidden
				footer={[
					<Button key="close" onClick={() => setIsStatusesOpen(false)}>
						Yopish
					</Button>,
					<Button
						className="crm-primary-btn"
						icon={<PlusOutlined />}
						key="add"
						onClick={() => openStatusForm()}
						type="primary"
					>
						Holat qo'shish
					</Button>,
				]}
				open={isStatusesOpen}
				title="Kanban sozlamalari"
				width={720}
				onCancel={() => setIsStatusesOpen(false)}
			>
				<div className="crm-lead-settings-head">
					<div>
						<strong>Lead holatlari</strong>
						<span>Ustun tartibi, rang va backend key sozlamalari</span>
					</div>
					<Select
						defaultValue={buildings.length > 0 ? buildings[0].name : undefined}
						placeholder="Bino tanlang"
						options={buildings.map((b) => ({ label: b.name, value: b.name }))}
					/>
				</div>

				<div className="crm-lead-status-list">
					{orderedStatuses.map((status) => (
						<div className="crm-lead-status-row" key={status.id}>
							<span className="crm-lead-status-order">{status.order}</span>
							<span className="crm-lead-status-dot" style={{ background: status.color }} />
							<div>
								<strong>{status.name}</strong>
								<span>{status.nameRu}</span>
							</div>
							<span className="crm-lead-color-cell">
								<i style={{ background: status.color }} />
								{status.color}
							</span>
							<div className="crm-lead-status-actions">
								<Tooltip title="Tahrirlash">
									<Button
										aria-label={`${status.name} tahrirlash`}
										icon={<EditOutlined />}
										onClick={() => openStatusForm(status)}
									/>
								</Tooltip>
								<Popconfirm
									cancelText="Bekor"
									okText="O'chirish"
									title="Holatni o'chirish?"
									onConfirm={() => handleDeleteStatus(status.id)}
								>
									<Tooltip title="O'chirish">
										<Button
											aria-label={`${status.name} o'chirish`}
											className="crm-danger-icon-btn"
											icon={<DeleteOutlined />}
										/>
									</Tooltip>
								</Popconfirm>
							</div>
						</div>
					))}
				</div>

				<p className="crm-lead-settings-note">
					Bu sozlama kanban ustunlari tartibi, rangi va nomlarini boshqaradi.
				</p>
			</Modal>

			<Modal
				cancelText="Bekor"
				centered
				className="crm-status-form-modal"
				destroyOnHidden
				okText={editingStatusId ? "Saqlash" : "Qo'shish"}
				open={isStatusFormOpen}
				title={editingStatusId ? "Holatni tahrirlash" : "Yangi holat"}
				onCancel={() => setIsStatusFormOpen(false)}
				onOk={handleSaveStatus}
			>
				<div className="crm-status-form">
					<div className="crm-lead-field">
						<span>Nomi UZ</span>
						<Input
							placeholder="Masalan: Yangi"
							value={statusDraft.name}
							onChange={(event) =>
								setStatusDraft((current) => ({ ...current, name: event.target.value }))
							}
						/>
					</div>
					<div className="crm-status-form-grid">
						<div className="crm-lead-field">
							<span>Nomi RU</span>
							<Input
								placeholder="Новый"
								value={statusDraft.nameRu}
								onChange={(event) =>
									setStatusDraft((current) => ({ ...current, nameRu: event.target.value }))
								}
							/>
						</div>
						<div className="crm-lead-field">
							<span>Rang</span>
							<div className="crm-status-color-control">
								<label className="crm-status-color-swatch">
									<span style={{ background: statusDraft.color }} />
									<input
										aria-label="Rang tanlash"
										type="color"
										value={statusDraft.color}
										onChange={(event) =>
											setStatusDraft((current) => ({ ...current, color: event.target.value }))
										}
									/>
								</label>
								<Input
									className="crm-status-color-input"
									value={statusDraft.color}
									onChange={(event) =>
										setStatusDraft((current) => ({ ...current, color: event.target.value }))
									}
								/>
							</div>
						</div>
					</div>
				</div>
			</Modal>

			<Modal
				cancelText="Bekor"
				destroyOnHidden
				okText="Qo'shish"
				open={isCreateOpen}
				title="Yangi lead"
				onCancel={() => setIsCreateOpen(false)}
				onOk={handleCreateLead}
			>
				<div className="crm-lead-form">
					<div className="crm-lead-field">
						<span>F.I.O</span>
						<Input
							placeholder="Mijoz ismi"
							value={draft.name}
							onChange={(event) =>
								setDraft((current) => ({ ...current, name: event.target.value }))
							}
						/>
					</div>
					<div className="crm-lead-form-grid">
						<div className="crm-lead-field">
							<span>Telefon</span>
							<UzPhoneInput
								value={draft.phone}
								onChange={(phone) => setDraft((current) => ({ ...current, phone }))}
							/>
						</div>
						<div className="crm-lead-field">
							<span>Email</span>
							<Input
								placeholder=""
								value={draft.email}
								onChange={(event) =>
									setDraft((current) => ({ ...current, email: event.target.value }))
								}
							/>
						</div>
					</div>
					<div className="crm-lead-form-grid">
						<div className="crm-lead-field">
							<span>Manba</span>
							<Select
								options={(Object.entries(sourceLabels) as [LeadSource, string][]).map(
									([value]) => ({ label: value, value })
								)}
								value={draft.source}
								onChange={(source) => setDraft((current) => ({ ...current, source }))}
							/>
						</div>
						<div className="crm-lead-field">
							<span>Mas'ul menejer</span>
							<Select
								options={managers.map((m) => ({ label: m.fullName, value: m.id }))}
								value={draft.managerId || undefined}
								onChange={(managerId) =>
									setDraft((current) => ({
										...current,
										managerId,
										managerName:
											managers.find((manager) => manager.id === managerId)?.fullName ?? "",
									}))
								}
							/>
						</div>
					</div>
					<div className="crm-lead-form-grid single">
						<div className="crm-lead-field">
							<span>Holat</span>
							<Select
								options={orderedStatuses.map((status) => ({
									label: status.name,
									value: status.id,
								}))}
								value={draft.statusId}
								onChange={(statusId) => setDraft((current) => ({ ...current, statusId }))}
							/>
						</div>
					</div>
				</div>
			</Modal>
		</div>
	);
}

export default LeadsKanbanPage;
